import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, CardHead } from "@/components/admin/kit";

/* Runtime controls for the Pro-Am Basketball sportsbook. Reads/writes the
   sb_settings flag doc via /admin/sportsbook-upa/flags, so the whole feature is
   managed here — no env edits, no redeploy. */
const SWITCHES = [
  ["enabled", "Pro-Am Basketball enabled", "Master switch — turns the whole section on or off."],
  ["publish", "Publish markets", "Operators can publish games to the Esports board."],
  ["accept", "Accept wagers", "Customers can place bets on published games."],
  ["outbound", "Outbound feed", "Pull HOF and Dunk matches and results from the partner API."],
];

export default function SportsbookUPAControls() {
  const [flags, setFlags] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get("/admin/sportsbook-upa/flags")
    .then(({ data }) => setFlags(data.flags)).catch(() => setFlags(null)), []);
  useEffect(() => { load(); }, [load]);

  const apply = async (key, val) => {
    if (busy) return;
    setBusy(key);
    try {
      const { data } = await api.post("/admin/sportsbook-upa/flags", { ...(flags || {}), [key]: val });
      setFlags(data.flags);
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update — super admin required");
    } finally { setBusy(null); }
  };

  const f = flags || { enabled: false, publish: false, accept: false, outbound: false };

  return (
    <Panel className="p-4">
      <CardHead title="Pro-Am Basketball sportsbook"
        right={<span className="text-[11px] text-[#71717a]">Runtime controls · super admin</span>} />
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        {SWITCHES.map(([key, label, desc]) => {
          const on = !!f[key];
          const locked = key !== "enabled" && !f.enabled;   // sub-flags need the master
          return (
            <div key={key}
              className={`flex items-center justify-between rounded-[10px] border border-[#1F1F23] bg-[#0E0E11] px-3 py-2 ${locked ? "opacity-50" : ""}`}>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-[#e4e4e7]">{label}</div>
                <div className="text-[11px] text-[#71717a]">{desc}</div>
              </div>
              <button disabled={busy === key || locked} onClick={() => apply(key, !on)}
                className="shrink-0 rounded-full px-3 py-1 text-[12px] font-bold"
                style={{ backgroundColor: on ? "#00D632" : "#2A2A30", color: on ? "#0A0A0A" : "#a1a1aa" }}>
                {busy === key ? "…" : on ? "ON" : "OFF"}
              </button>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-[12px] leading-relaxed text-[#71717a]">
        With the master on and Publish on, an operator publishes a game and it appears under
        Esports → Pro-Am Basketball. Accept lets customers place bets. Everything is controlled
        here — no environment changes needed.
      </p>
    </Panel>
  );
}
